import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, Skeleton, Tooltip, Checkbox, message, Radio } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ColorResult } from 'react-color';
import { StyledForm, StyledPreview } from './StyledForm';
import { ButtonActionPage, SelectOption } from '../../../atoms';
import { UploadImage } from '../../../molecules/upload';
import {
  CATEGORY,
  HomeGroupKey,
  HomeType,
  HomeWidgetStatus,
  HomeWidgetsDefaultForm,
  HomeWidgetsForm,
} from '../../../../types/home';
import { HomeWidgetsRecord } from '../../../../repository/mytm/home/homeWidgetsRepository';
import { HOME } from '../../../../constants/define/translation';
import { JUST_IN_CATEGORIES, DEFAULT_HOME, APP_DEEP_LINK_FEATURES } from '../../../../constants/home';
import { EcomProductContext } from '../../../../repository/context/ecomProductContext';
import { getProductsInCategoryRepository } from '../../../../repository/mytm/ecom/ecomRepository';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { DEFAULT_BACKGROUND_OPACITY, TEXT_COLOR_BLACK, TEXT_COLOR_WHITE } from '../../../../constants/common';
import { WrapperDesign } from '../../../molecules/wrapper-design/WrapperDesign';
import { Align } from '../../../../types/common';
import { onValidator } from '../../../../utils/helper';
import HomeTileRecordId from '../HomeTileRecordId';
import { JustInPreview } from '../../../molecules/preview/tiles';
import { CountryContext } from '../../../../repository/context/countryContext';

const { Option } = Select;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const azureCDN = process.env.REACT_APP_AZURE_CDN;
interface FormJustInProps {
  onSaveHomeWidgets: (values: any) => void;
  record?: HomeWidgetsRecord;
}

const DEFAULT_PROMOTION = {
  name: undefined,
  heading: DEFAULT_HOME.PROMOTION.HEADING,
  description: undefined,
  productId: undefined,
  ctaText: undefined,
  ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK,
  category: CATEGORY.CMS,
  contentType: undefined,
  contentId: undefined,
  contentFormat: undefined,
  backgroundImage: undefined,
  title: undefined,
  colorOpacity: DEFAULT_BACKGROUND_OPACITY,
  buttonAlign: Align.LEFT,
  textAlign: Align.LEFT,
  countries: ['USA', 'CAN'],
};

export const FormJustIn = (props: FormJustInProps) => {
  const ecomProductContext = useContext(EcomProductContext);
  const countryContext = useContext(CountryContext);
  const { t } = useTranslation(HOME);
  const { onSaveHomeWidgets, record } = props;
  const [form] = Form.useForm();
  const [dataForm, setDataForm] = useState<HomeWidgetsForm>(DEFAULT_PROMOTION);
  const [category, setCategory] = useState<CATEGORY>(CATEGORY.CMS);
  const [visibilityTextUploadImage, setVisibilityTextUploadImage] = useState<boolean>(true);
  const [isShowColorPicker, setIsShowColorPicker] = useState(false);
  const [textColor, setTextColor] = useState<string | undefined>(TEXT_COLOR_BLACK);
  const [isRadio, setIsRadio] = useState<boolean>(false);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });
  const defaultCountryValues: any[] = countryContext?.countryList?.map(item => {
    return item?.code;
  });

  const onFinish = useCallback(
    (values: HomeWidgetsForm & HomeWidgetsDefaultForm) => {
      onSaveHomeWidgets({
        ...values,
        status: record?.status || HomeWidgetStatus.INACTIVE,
        groupKey: HomeGroupKey.HOME_TILES,
        type: HomeType.PROMOTION,
        backgroundColor: record?.backgroundColor,
        extraData: record?.extraData || {},
        show: record?.show,
        textColor,
        countries: values?.countries.toString(),
        colorOpacity: isNaN(Number(values?.colorOpacity)) ? 0 : Number(values?.colorOpacity),
        // buttonAlign: values?.category !== CATEGORY.NO_CTA ? values?.buttonAlign : Align.LEFT,
        // textAlign: values?.category !== CATEGORY.NO_CTA ? values?.textAlign : Align.LEFT,
      });
    },
    [record, onSaveHomeWidgets, textColor],
  );

  const onChangeInput = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  const onChangeInputUploadImage = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
      setVisibilityTextUploadImage(false);
    },
    [form],
  );

  const onGetUrlUploaded = useCallback(
    (url: string) => {
      const newUrlCDN = azureCDN ? url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN) : url;
      form.setFieldsValue({
        ...form.getFieldsValue(),
        backgroundImage: newUrlCDN,
      });
      setDataForm(old => ({
        ...old,
        backgroundImage: newUrlCDN,
      }));
      setVisibilityTextUploadImage(true);
    },
    [form],
  );

  const onShowColorPicker = useCallback(() => {
    setIsShowColorPicker(old => !old);
  }, []);

  const onCloseColorPicker = useCallback(() => {
    setIsShowColorPicker(false);
  }, []);

  const handleChangeColor = useCallback((value: ColorResult) => {
    setTextColor(value?.hex);
  }, []);

  const handleGetEcomProduct = useCallback(async (country?: string) => {
    let productList;
    try {
      const dataProductEcom: any = await getProductsInCategoryRepository({
        country,
      });
      if (dataProductEcom) {
        const productEcomList = dataProductEcom?.hits.map((product: any, index: number) => ({
          index,
          id: product?.representedProduct?.id,
          image: product?.image?.link,
          name: product?.productName || '',
        }));
        ecomProductContext.updateProductList(productEcomList);

        productList = productEcomList;
      } else {
        ecomProductContext.updateProductList([]);
      }
    } catch (error) {
      ecomProductContext.updateProductList([]);
      message.error(MESSAGE_ERROR.M02);
    }

    if (record && record.category === CATEGORY.ECOM_PRODUCT) {
      const product = productList.find((x: any) => x.id === record?.productId);
      if (product && productList && productList.length > 0) {
        form.setFieldsValue({
          productId: record?.productId,
        });
      }
    }
  }, []);

  const onHandleSelectCategory = useCallback(
    (value: CATEGORY) => {
      if (!value) {
        return;
      }

      if (value === CATEGORY.ECOM_PRODUCT) {
        form.setFieldsValue({
          countries: 'USA',
        });
      } else {
        form.setFieldsValue({
          countries: defaultCountryValues,
        });
      }

      setIsRadio(value === CATEGORY.ECOM_PRODUCT);

      setCategory(value);
      let formChanged: HomeWidgetsForm | HomeWidgetsDefaultForm | { featureDeepLink?: string } = {};
      if (record && record?.category === value) {
        formChanged = {
          name: record.name,
          heading: record.heading,
          title: record.title,
          description: record.description,
          backgroundImage: record.backgroundImage,
          productId: record.productId,
          ctaText: record.ctaText,
          ctaLink: record.ctaLink,
          category: record?.category,
          contentType: record?.contentType,
          contentId: record?.contentId,
          contentFormat: record?.contentFormat,
          colorOpacity: record?.colorOpacity || DEFAULT_BACKGROUND_OPACITY,
          buttonAlign: record?.buttonAlign || Align.LEFT,
          textAlign: record?.textAlign || Align.LEFT,
          textColor: record?.textColor || TEXT_COLOR_BLACK,
          countries: value === CATEGORY.ECOM_PRODUCT ? record?.countries : record?.countries?.split(','),
          featureDeepLink:
            record?.category === CATEGORY.APP_DEEP_LINK && record?.ctaLink
              ? APP_DEEP_LINK_FEATURES.find(x => x.ctaLink === record?.ctaLink)?.value
              : undefined,
          ...toDataFormChanged(value, record),
        };
        setTextColor(record?.textColor);
      } else {
        formChanged = {
          ...formChanged,
          ...toDataFormChanged(value),
        };
      }
      form.setFieldsValue(formChanged);
      setDataForm(old => ({
        ...old,
        ...formChanged,
      }));
      if (value === CATEGORY.ECOM_PRODUCT) {
        const country = form.getFieldValue('countries');
        handleGetEcomProduct(country).then(r => r);
      }
    },
    [form, handleGetEcomProduct],
  );

  const toDataFormChanged = (value: CATEGORY, recordWillEdit?: any) => {
    let formChanged = {};
    if (value === CATEGORY.CMS) {
      formChanged = {
        productId: undefined,
        ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK,
      };
    } else if (value === CATEGORY.ECOM_PRODUCT) {
      formChanged = {
        contentType: undefined,
        contentId: undefined,
        contentFormat: undefined,
        ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK_PRODUCT,
      };
    } else if ([CATEGORY.APP_DEEP_LINK, CATEGORY.WEB_LINK, CATEGORY.NO_CTA].includes(value)) {
      formChanged = {
        contentType: undefined,
        contentId: undefined,
        contentFormat: undefined,
        productId: undefined,
        ctaLink: undefined,
        ctaText: recordWillEdit?.ctaText,
      };

      if (value !== CATEGORY.NO_CTA) {
        formChanged = {
          ...formChanged,
          ctaLink: recordWillEdit?.ctaLink,
        };
      }
      setVisibilityTextUploadImage(false);
    }

    return formChanged;
  };

  const onHandleSelectDeepLink = useCallback(
    (value: string) => {
      form.setFieldsValue({
        ctaLink: value ? APP_DEEP_LINK_FEATURES.find(x => x.value === value)?.ctaLink : undefined,
      });
    },
    [form],
  );

  useEffect(() => {
    if (record) {
      if (record?.category) {
        onHandleSelectCategory(record.category);
      }
    } else {
      form.setFieldsValue(DEFAULT_PROMOTION);
    }
  }, [record]);

  const onChangeCountry = async (event: any) => {
    const value = event?.target?.value;
    await handleGetEcomProduct(value);
    form.setFieldsValue({
      productId: undefined,
    });
  };

  const defaultProperties = {
    ...DEFAULT_HOME.PROMOTION,
    countries: isRadio ? 'USA' : defaultCountryValues,
  };

  return (
    <>
      <Form layout="vertical" form={form} onFinish={onFinish} name="from-just-in" initialValues={defaultProperties}>
        <Row>
          <StyledPreview span={9}>
            <JustInPreview
              values={{
                ...dataForm,
                textColor,
              }}
              isPreview
              height="100"
            />
          </StyledPreview>
          <StyledForm span={15}>
            <div className="form-scroll">
              {record?.id && <HomeTileRecordId id={record?.id} />}
              <Form.Item
                name="name"
                label={t('form.cardName')}
                rules={[{ required: true, message: t('validate.nameBlank') }]}
              >
                <Input placeholder={t('form.cardName')} maxLength={255} />
              </Form.Item>
              <Form.Item
                name="heading"
                label={t('form.labelOfCard')}
                rules={[{ required: true, message: t('validate.labelBlank') }]}
              >
                <Input placeholder={t('form.labelOfCardPlaceholder')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <Form.Item
                name="backgroundImage"
                label={t('form.image')}
                rules={[{ required: true, message: t('validate.imageBlank') }]}
                className={`image-backgroundImage ${visibilityTextUploadImage ? '' : 'fileName-bgImage-hidden'}`}
              >
                <Input
                  placeholder={t('form.imagePlaceholder')}
                  suffix={<UploadImage onGetUrlUploaded={onGetUrlUploaded} />}
                  onChange={onChangeInputUploadImage}
                />
              </Form.Item>
              <Form.Item name="title" label={t('form.title')}>
                <Input placeholder={t('form.titlePlaceholder')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <Form.Item name="description" label={t('form.description')}>
                <Input placeholder={t('form.description')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <SelectOption
                modeForm
                name="category"
                label={t('form.category')}
                rules={[onValidator(JUST_IN_CATEGORIES)]}
                values={JUST_IN_CATEGORIES}
                onChange={onHandleSelectCategory}
                textDisplay="text"
              />
              <Form.Item
                name="countries"
                label={t('form.countries')}
                rules={[{ required: true, message: t('validate.countryBlank') }]}
              >
                {isRadio ? (
                  <Radio.Group options={countryOptions} onChange={onChangeCountry} />
                ) : (
                  <Checkbox.Group options={countryOptions} />
                )}
              </Form.Item>

              {category === CATEGORY.CMS ? (
                <Row>
                  <Col span={11}>
                    <Form.Item name="contentId" label={t('form.content')}>
                      <Input placeholder={t('form.contentId')} maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col span={11} offset={2} className="">
                    <Form.Item name="contentFormat" label=" ">
                      <Input placeholder={t('form.contentFormat')} maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col span={1} className="">
                    <Form.Item name="contentType" label=" " hidden>
                      <Input placeholder={t('form.contentType')} maxLength={50} />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <>
                  <Form.Item name="contentId" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="contentType" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="contentFormat" hidden>
                    <Input />
                  </Form.Item>
                </>
              )}
              {category && [CATEGORY.ECOM_PRODUCT].includes(category) ? (
                <Form.Item label={t('form.productId')}>
                  {!ecomProductContext.productList && (
                    <>
                      <Skeleton active paragraph={{ rows: 0 }} />
                    </>
                  )}
                  {ecomProductContext.productList && ecomProductContext.productList.length === 0 && (
                    <Form.Item name="productId">
                      <Input placeholder={t('form.productIdPlaceholder')} maxLength={255} />
                    </Form.Item>
                  )}
                  {ecomProductContext.productList && ecomProductContext.productList.length > 0 && (
                    <Form.Item name="productId">
                      <Select
                        showSearch
                        placeholder={t('form.productIdPlaceholder')}
                        optionFilterProp="children"
                        filterOption={(input, option: any) =>
                          option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                      >
                        {ecomProductContext.productList.map((item: any, index: any) => (
                          <Option value={item?.id || ''} key={item.index} className="option-content-product-ecom">
                            <div className="thumb-product-ecom">
                              <img src={item?.image} alt="product" />
                            </div>
                            <Tooltip title={item.name}>
                              <span className="content-product-ecom-id">{item.id}</span>
                            </Tooltip>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Form.Item>
              ) : (
                <Form.Item name="productId" hidden>
                  <Input />
                </Form.Item>
              )}
              {category && [CATEGORY.APP_DEEP_LINK].includes(category) && (
                <Row>
                  <Col span={11} className="buttonLink">
                    <SelectOption
                      modeForm
                      name="featureDeepLink"
                      label={t('form.featureDeepLink')}
                      rules={[onValidator(APP_DEEP_LINK_FEATURES)]}
                      values={APP_DEEP_LINK_FEATURES}
                      onChange={onHandleSelectDeepLink}
                      showSearch
                      allowClear
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col span={11}>
                  <Form.Item name="ctaText" label={t('form.button')}>
                    <Input placeholder={t('form.buttonText')} maxLength={50} onChange={onChangeInput} />
                  </Form.Item>
                </Col>
              </Row>
              {category && [CATEGORY.CMS, CATEGORY.APP_DEEP_LINK, CATEGORY.ECOM_PRODUCT].includes(category) && (
                <Row>
                  <Form.Item name="ctaLink" hidden>
                    <Input placeholder={t('form.buttonLink')} maxLength={255} disabled readOnly />
                  </Form.Item>
                </Row>
              )}
              {category && [CATEGORY.WEB_LINK].includes(category) && (
                <Row>
                  <Col span={24}>
                    <Form.Item name="ctaLink" rules={[{ required: true, message: t('validate.linkBlank') }]}>
                      <Input placeholder={t('form.link')} maxLength={255} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <WrapperDesign
                isShowColorPicker={isShowColorPicker}
                onChangeInput={onChangeInput}
                onShowColorPicker={onShowColorPicker}
                onCloseColorPicker={onCloseColorPicker}
                textColor={textColor || TEXT_COLOR_WHITE}
                handleChangeColor={handleChangeColor}
              />
            </div>
            <ButtonActionPage
              text={record ? t('btnUpdateCard') : t('btnCreateCard')}
              id="1"
              className="btn-submit-form-home"
              htmlType="submit"
              icon={!record ? <FileAddOutlined /> : undefined}
            />
          </StyledForm>
        </Row>
      </Form>
    </>
  );
};
